import {
  Opportunity,
  OpportunityApplicant,
} from '@modules/opportunities/entities/opportunity/opportunity.entity';
import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { getMissingBankApplicationFields } from '@modules/opportunities/entities/opportunity/api/dto/missingBankApplicationFields.dto';
import { useTranslation } from 'react-i18next';
import useUpdateMissingBankApplicationsFields from '@modules/opportunities/entities/opportunity/mutation/useUpdateMissingBankApplicationsFields';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import {
  useContext, useEffect, useState
} from 'react';
import useGetMissingBankApplicationsFields from '@modules/opportunities/entities/opportunity/query/useGetMissingBankApplicationsFields';
import { UnsavedChangesSlido } from '@modules/opportunities/presentation/pages/CaseCreation/features/UnsavedChangesSlido';
import { useDisclosure } from '@mantine/hooks';
import { CaseCreationContext } from '@modules/opportunities/presentation/pages/CaseCreation';
import {
  SAVE_ACTION_TYPE,
  SaveActionType,
} from '@modules/opportunities/presentation/pages/const';
import { EmptyFieldsSlido } from '@modules/opportunities/presentation/pages/CaseCreation/features/EmptyFieldsSlido';
import { Image } from '@mantine/core';
import { gifsPath } from '@shared/css.const';
import Tooltip from '@components/Tooltip';
import { SectionForm } from './components/SectionForm';
import { EmptyBankForm } from './components/EmptyBankForm';

type BankFormTypes = {
  applicants: OpportunityApplicant[];
  missingFields: getMissingBankApplicationFields[];
  opportunity: Opportunity;
  isAllDocumentValid: boolean;
};

const baseTranslation = 'opportunity.caseSubmission.documentSubmission.fillBankForms';

export const BankForm = ({
  applicants,
  missingFields,
  opportunity,
  isAllDocumentValid,
}: BankFormTypes) => {
  const { t } = useTranslation();
  const { goToNextState } = useContext(CaseCreationContext);
  const [opened, { close, open }] = useDisclosure(false);
  const opportunityId = applicants[0]?.opportunity_external_id!;

  const [saveActionType, setSaveActionType] = useState<null | SaveActionType>(
    null
  );
  const [isPendingFormCompleted, setIsPendingFormCompleted] = useState(
    applicants.map((app) => ({
      id: app.opportunity_applicant_external_id,
      isCompleted: false,
      data: {},
      numberOfDirtyFields: 0,
    }))
  );

  const { mutateAsync: updateMissingFields, isPending } = useUpdateMissingBankApplicationsFields();

  const { refetch } = useGetMissingBankApplicationsFields(opportunityId);

  useEffect(() => {
    // Track view
    trackAmplitudeEvent(USER_EVENTS.CASE.CASE_FILL_BANK_FORMS_SECTION_VIEWED, {
      addToContext: true,
      client_id: opportunity.client_external_id,
      case_id: opportunity.opportunity_external_id,
      banks: opportunity.bank_applications.map(
        (bankApplication) => bankApplication.bank_details.external_id
      ),
    });
  }, [opportunity]);

  const handleSave = async () => {
    if (isPendingFormCompleted) {
      const mergedData = isPendingFormCompleted.reduce(
        (acc, next) => ({ ...acc, ...next.data }),
        {}
      );
      const totalNumberOfDirtyFields = isPendingFormCompleted.reduce(
        (acc, next) => acc + next.numberOfDirtyFields,
        0
      );
      if (totalNumberOfDirtyFields > 0) {
        await updateMissingFields({
          opportunityId,
          body: mergedData,
          numberOfDirtyFields: totalNumberOfDirtyFields,
        });
        const numOfAllFields = Object.keys(mergedData).length;
        const numOfFilledField = Object.values(mergedData).filter(
          (field) => !(field === '' || field === null)
        ).length;
        const percentage_of_fields_filled = `${Math.ceil((numOfFilledField / numOfAllFields) * 100)}%`;
        trackAmplitudeEvent(USER_EVENTS.CASE.CASE_PENDING_FIELDS_NEXT_CLICKED, { percentage_of_fields_filled });

        // Track save
        trackAmplitudeEvent(USER_EVENTS.CASE.CASE_FILL_BANK_FORMS_SAVED, {
          useContext: true,
          addToContext: true,
          total_fields: numOfAllFields,
          pending_fields: numOfAllFields - numOfFilledField,
        });
      }
    }
  };

  const updateCompletion = (
    applicantId: string,
    isCompleted: boolean,
    data: Record<string, any>,
    numberOfDirtyFields: number
  ) => {
    setSaveActionType(null);
    setIsPendingFormCompleted((prev) =>
      prev?.map((item) => {
        if (item.id === applicantId) {
          return {
            ...item,
            isCompleted,
            data,
            numberOfDirtyFields,
          };
        }
        return item;
      }));
  };

  const saveAndProceed = async () => {
    await handleSave();
    goToNextState();
    refetch();
  };

  const handleNext = async () => {
    if (isPendingFormCompleted?.every((item) => item.isCompleted)) {
      await saveAndProceed();
    } else {
      open();
    }
  };

  if (!missingFields || missingFields.length === 0) {
    return <EmptyBankForm opportunityId={ opportunityId } />;
  }

  const isDirty = () => {
    const hasDataChanged = isPendingFormCompleted.reduce(
      (acc, next) => acc + next.numberOfDirtyFields,
      0
    ) > 0;

    if (saveActionType && hasDataChanged) {
      return false;
    }

    return hasDataChanged;
  };

  return (
    <Flex gap='6' direction='column'>
      <Flex justify='space-between'>
        <Flex direction='column' gap='2' mb='4'>
          <Text size='2xl'>{t(`${baseTranslation}.title`)}</Text>
          <Text size='lg' color='neutral.500'>
            {t(`${baseTranslation}.subtitle`)}
          </Text>
        </Flex>
        {!isAllDocumentValid && (
          <Tooltip
            label={ (
              <Flex
                direction='column'
                justify='center'
                align='center'
                gap='2'
                p='2'
              >
                <Text size='md'>
                  {t(`${baseTranslation}.autoFillInProgressTitle`)}
                </Text>
                <Text size='md'>
                  {t(`${baseTranslation}.autoFillInProgressDesc`)}
                </Text>
              </Flex>
            ) }
          >
            <Flex direction='column' justify='center' align='center' gap='1'>
              <Box w='10' h='10'>
                <Image src={ `${gifsPath}/writing.gif` } />
              </Box>
              <Text size='md'>
                {t(`${baseTranslation}.autoFillInProgressTitle`)}
              </Text>
            </Flex>
          </Tooltip>
        )}
      </Flex>
      <Flex
        justify='space-between'
        align='center'
        gap='1'
        backgroundColor='blue.100'
        borderRadius='4'
        py='2'
        px='6'
      >
        <Text fontWeight='500'>{t(`${baseTranslation}.optional.title`)}</Text>
        <Button variant='outline' onClick={ goToNextState }>
          {t(`${baseTranslation}.optional.skipBtn`)}
        </Button>
      </Flex>
      <Flex flexDirection='column' gap='2'>
        {applicants.map((applicant) => (
          <SectionForm
            key={ applicant.opportunity_applicant_external_id }
            applicant={ applicant }
            missingFields={ missingFields.find(
              (item) => item.applicant_type === applicant.applicant_type
            ) }
            updateCompletion={ updateCompletion }
          />
        ))}
      </Flex>
      <Flex justify='space-between' gap='2' mt='4'>
        <Button
          onClick={ async () => {
            setSaveActionType(SAVE_ACTION_TYPE.SAVE);
            await handleSave();
          } }
          disabled={ isPending }
          loading={ isPending && saveActionType === SAVE_ACTION_TYPE.SAVE }
          variant='outline'
        >
          {t('clients.formSections.saveInfoButton')}
        </Button>
        <Button
          type='submit'
          data-test='proceed-btn'
          disabled={ isPending }
          loading={ isPending && saveActionType === SAVE_ACTION_TYPE.SAVE_AND_PROCEED }
          variant={ isPending && saveActionType === SAVE_ACTION_TYPE.SAVE_AND_PROCEED
            ? 'secondary'
            : 'primary' }
          onClick={ () => {
            // Track proceed
            trackAmplitudeEvent(
              USER_EVENTS.CASE.CASE_FILL_BANK_FORMS_PROCEEDED,
              { useContext: true }
            );

            setSaveActionType(SAVE_ACTION_TYPE.SAVE_AND_PROCEED);
            handleNext();
          } }
        >
          {t('clients.formSections.proceedToBankSubmissionBtn')}
        </Button>
      </Flex>
      <EmptyFieldsSlido
        opened={ opened }
        close={ close }
        save={ saveAndProceed }
        isLoading={ isPending }
      />
      <UnsavedChangesSlido
        isDirty={ isDirty() && !opened }
        save={ async () => {
          await handleSave();
          refetch();
        } }
        isLoading={ isPending }
      />
    </Flex>
  );
};
